import { createLogger, isWebos } from '@tivio/common'
import {
    QerkoPaymentInfo,
    TivioAuth,
    TivioComponents,
    TivioGetters,
    TivioHooks,
    TivioSubscriptions,
    useTivioData,
} from '@tivio/sdk-react'
import React, { createContext, PropsWithChildren, useEffect, useState } from 'react'

import { SplashScreen } from '../SplashScreen'


interface BundleContextState {
    state: 'loading' | 'error' | 'ready'
    purchaseVideoWithQerko: (videoId: string, monetizationId: string) => Promise<QerkoPaymentInfo>
    auth: TivioAuth
    getters: TivioGetters
    hooks: TivioHooks
    subscriptions: TivioSubscriptions
    components: TivioComponents
}

const BundleContext = createContext<BundleContextState | null>(null)
const logger = createLogger('AppBundleContextProvider')

const useWebosAutoLogout = () => {
    const bundle = useTivioData()
    const [isAutoLogoutDone, setIsAutoLogoutDone] = useState(false)

    useEffect(() => {
        (async function() {
            if (!bundle.auth) {
                return
            }

            try {
                if (isWebos()) {
                    const STORAGE_NAME = 'SHOULD_KEEP_LOGGED_IN'
                    const STORAGE_VALUE = 'TRUE' // only this value or it is not set
                    const currentStorageValue = localStorage.getItem(STORAGE_NAME)

                    if (currentStorageValue !== STORAGE_VALUE) {
                        logger.info('It is the first run of this application, going to perform logout because some '
                            + 'devices (e.g. LG TVs) do not clear all storages while application un-installation.',
                        )
                        await bundle.auth.signOut()
                        localStorage.setItem(STORAGE_NAME, STORAGE_VALUE)
                    }
                }
            } catch (error) {
                logger.error(
                    'Error during auto-logout after application\'s first run, ignoring this error to prevent blocking start of the application.',
                    error,
                )
            }

            setIsAutoLogoutDone(true)
        })()
    }, [bundle.auth])

    return { isAutoLogoutDone }
}

/**
 * Bundle wrapper component - provides necessary bundle methods for tv-web
 * App should not run if failed to get all necessary bundle methods
 */
const BundleProvider: React.FC<PropsWithChildren> = (props) => {
    const bundle = useTivioData()
    const { isAutoLogoutDone } = useWebosAutoLogout()

    if (!bundle || bundle.state === 'loading' || !isAutoLogoutDone) {
        return (
            <SplashScreen/>
        )
    }

    const {
        auth,
        state,
        components,
        getters,
        hooks,
        subscriptions,
        purchaseVideoWithQerko,
    } = bundle
    const errors = []

    if (!auth) {errors.push('failed to load auth')}
    if (!components) {errors.push('failed to load components')}
    if (!getters) {errors.push('failed to load getters')}
    if (!subscriptions) {errors.push('failed to load subscriptions')}
    if (!purchaseVideoWithQerko) {errors.push('failed to load purchaseVideoWithQerko')}

    if (
        (errors.length > 0
            || !auth
            || !components
            || !getters
            || !subscriptions
            || !purchaseVideoWithQerko
            || !hooks
            || state === 'error')
    ) {
        logger.error('Failed to load bundle with errors: ', errors)

        return (
            <h1>app error</h1>
        )
    }

    return (
        <BundleContext.Provider
            value={{
                auth,
                state,
                components,
                hooks,
                getters,
                subscriptions,
                purchaseVideoWithQerko,
            }}
        >
            {props.children}
        </BundleContext.Provider>
    )
}

export {
    BundleContext,
    BundleProvider,
}

export type {
    BundleContextState,
}
